import { Building2, BedDouble, Car, ChefHat, Bell } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/images";

const AMENITIES = [
  { icon: Building2, title: "45th-Floor Skyline Balcony", text: "Floor-to-ceiling glass over downtown Atlanta." },
  { icon: BedDouble, title: "King Master Suite", text: "Italian linens, blackout blinds, skyline wakeup." },
  { icon: Car, title: "Private Garage Bay", text: "Your rental waits downstairs, charged and detailed." },
  { icon: ChefHat, title: "Chef's Kitchen", text: "Full suite, wine fridge, private-chef on call." },
];

export const CondoSection = ({ condo, onReserve }) => (
  <section id="condo" data-testid="condo-section" className="py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
      <Reveal>
        <p className="eyebrow">The Residence · Airbnb-Style Stay</p>
        <h2 className="mt-4 font-serif text-3xl font-semibold text-slate-100 sm:text-4xl lg:text-5xl">
          The Skyline <span className="gold-gradient-text italic">Penthouse</span>
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-400">
          Two bedrooms, two baths, and the whole of downtown at your feet. Book it
          alone — or pair it with the fleet and never touch the ground floor.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
        <Reveal className="md:col-span-7 md:row-span-2" delay={0.05}>
          <div className="spotlight-card group h-full min-h-[420px]">
            <img
              src={IMAGES.penthouseView}
              alt="Penthouse view over the Atlanta skyline"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-8">
              <p className="font-mono text-[11px] tracking-[0.3em] text-gold uppercase">Downtown Atlanta, GA</p>
              <p className="mt-2 font-serif text-2xl text-slate-100">45 Floors Above Peachtree</p>
            </div>
          </div>
        </Reveal>

        <Reveal className="md:col-span-5" delay={0.12}>
          <div className="spotlight-card flex h-full flex-col justify-between p-8">
            <div>
              <p className="eyebrow">Nightly Rate</p>
              <p className="mt-3 font-serif text-5xl text-slate-100">
                ${condo?.nightly_rate ?? 289}
                <span className="ml-2 font-sans text-sm text-zinc-500">/ night</span>
              </p>
              <p className="mt-3 font-mono text-[11px] tracking-[0.2em] text-zinc-500 uppercase">
                + ${condo?.cleaning_fee ?? 150} cleaning · Sleeps {condo?.max_guests ?? 4}
              </p>
            </div>
            <button
              data-testid="condo-reserve-btn"
              onClick={() => onReserve("condo", condo?.id ?? "skyline-penthouse")}
              className="mt-8 w-full rounded-full bg-gold px-6 py-3.5 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase transition-all hover:bg-gold-bright"
            >
              Reserve the Penthouse
            </button>
          </div>
        </Reveal>

        <Reveal className="md:col-span-5" delay={0.18}>
          <div className="spotlight-card flex h-full items-start gap-5 p-8">
            <div className="rounded-full border border-gold/30 p-3 text-gold">
              <Bell size={22} />
            </div>
            <div>
              <p className="font-display text-lg text-slate-100">24/7 VIP Concierge</p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                Late checkout, restaurant tables, club entry, stocked fridge on arrival — one text away.
              </p>
            </div>
          </div>
        </Reveal>

        {AMENITIES.map((a, i) => (
          <Reveal key={a.title} className="md:col-span-3" delay={0.08 * i}>
            <div className="spotlight-card h-full p-6" data-testid={`condo-amenity-${i}`}>
              <a.icon size={20} className="text-gold" />
              <p className="mt-4 font-display text-sm font-medium text-slate-200">{a.title}</p>
              <p className="mt-2 text-xs leading-relaxed text-zinc-500">{a.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
